import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowRight, 
  GitBranch, 
  ShieldCheck, 
  MessageSquare, 
  HandshakeIcon,
  Building2,
  Check,
  Bot,
  ChevronRight,
  Send,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Workflow data
const workflowData = {
  'pr-governance': {
    title: 'PR Governance',
    subtitle: 'Automated code review and merge decision workflows',
    icon: GitBranch,
    color: '#1A3A8F',
    problem: 'Code review bottlenecks slow down engineering velocity. Manual reviews are inconsistent, and policy enforcement is reactive rather than proactive.',
    solution: 'Liquicent automates PR governance with AI agents that analyze code quality, enforce policies, and coordinate reviewer assignments—all recorded in the Unified Transaction Graph.',
    steps: [
      'Developer submits pull request',
      'PR Verification Agent analyzes code changes',
      'Security and compliance policies evaluated',
      'Appropriate reviewers auto-assigned',
      'Feedback synthesized and presented',
      'Merge decision recorded and executed',
    ],
    agents: [
      { name: 'PR Verification Agent', description: 'Analyzes code for quality, security, and best practices' },
      { name: 'Reviewer Routing Agent', description: 'Assigns optimal reviewers based on expertise and availability' },
      { name: 'Compliance Policy Agent', description: 'Enforces coding standards and security policies' },
    ],
    benefits: [
      '70% reduction in review cycle time',
      'Consistent policy enforcement across all PRs',
      'Complete audit trail for compliance',
      'Improved code quality and security',
    ],
  },
  'security-compliance': {
    title: 'Security & Compliance',
    subtitle: 'Continuous security monitoring and compliance automation',
    icon: ShieldCheck,
    color: '#2CB1A1',
    problem: 'Security reviews are manual, slow, and often happen too late in the development cycle. Compliance documentation is fragmented and hard to maintain.',
    solution: 'Liquicent embeds security and compliance checks directly into your workflows, catching issues early and maintaining continuous compliance documentation.',
    steps: [
      'Code or configuration change detected',
      'Security Audit Agent scans for vulnerabilities',
      'Compliance requirements mapped to changes',
      'Risk assessment generated',
      'Remediation recommendations provided',
      'Compliance status updated in real-time',
    ],
    agents: [
      { name: 'Security Audit Agent', description: 'Scans code and infrastructure for security vulnerabilities' },
      { name: 'Compliance Policy Agent', description: 'Maps changes to regulatory requirements' },
      { name: 'Risk Assessment Agent', description: 'Evaluates and prioritizes security risks' },
    ],
    benefits: [
      '90% faster security reviews',
      'Continuous compliance monitoring',
      'Automated audit documentation',
      'Proactive vulnerability detection',
    ],
  },
  'customer-support': {
    title: 'Customer Support',
    subtitle: 'Intelligent ticket routing and resolution automation',
    icon: MessageSquare,
    color: '#1A3A8F',
    problem: 'Support tickets are misrouted, responses are inconsistent, and agents spend too much time searching for solutions instead of helping customers.',
    solution: 'Liquicent automates ticket classification, knowledge retrieval, and response generation while maintaining human oversight for complex issues.',
    steps: [
      'Customer submits support ticket',
      'Ticket Classification Agent categorizes and prioritizes',
      'Knowledge Retrieval Agent finds relevant solutions',
      'Response draft generated for review',
      'Agent reviews and sends response',
      'Resolution recorded and knowledge base updated',
    ],
    agents: [
      { name: 'Ticket Classification Agent', description: 'Categorizes tickets by type, urgency, and required expertise' },
      { name: 'Knowledge Retrieval Agent', description: 'Searches knowledge base for relevant solutions' },
      { name: 'Response Generation Agent', description: 'Drafts customer responses based on context and history' },
    ],
    benefits: [
      '50% reduction in first response time',
      'Consistent quality across all interactions',
      'Automatic knowledge base enrichment',
      'Improved customer satisfaction scores',
    ],
  },
  'sales-deal-desk': {
    title: 'Sales Deal Desk',
    subtitle: 'Streamlined deal approval and pricing governance',
    icon: HandshakeIcon,
    color: '#2CB1A1',
    problem: 'Deal approvals take too long, pricing exceptions lack consistency, and sales teams lose momentum waiting for manual reviews.',
    solution: 'Liquicent automates deal validation, pricing compliance checks, and approval routing to accelerate sales cycles while maintaining governance.',
    steps: [
      'Sales rep submits deal for approval',
      'Deal Validation Agent reviews terms and conditions',
      'Pricing compliance automatically checked',
      'Risk factors evaluated and scored',
      'Approval routed to appropriate stakeholders',
      'Deal approved and logged in transaction graph',
    ],
    agents: [
      { name: 'Deal Validation Agent', description: 'Reviews deal terms against company policies' },
      { name: 'Pricing Compliance Agent', description: 'Ensures pricing follows approved guidelines' },
      { name: 'Approval Routing Agent', description: 'Routes deals to appropriate approvers based on value and risk' },
    ],
    benefits: [
      '60% faster deal approval cycles',
      'Consistent pricing governance',
      'Reduced revenue leakage',
      'Complete deal audit trail',
    ],
  },
  'vendor-onboarding': {
    title: 'Vendor Onboarding',
    subtitle: 'Automated vendor risk assessment and onboarding',
    icon: Building2,
    color: '#1A3A8F',
    problem: 'Vendor onboarding is slow, manual, and inconsistent. Risk assessments are point-in-time snapshots that quickly become outdated.',
    solution: 'Liquicent automates vendor risk assessment, document verification, and compliance monitoring for continuous vendor governance.',
    steps: [
      'Vendor application submitted',
      'Vendor Risk Agent assesses initial profile',
      'Compliance documents collected and verified',
      'Contract Review Agent analyzes terms',
      'Risk score calculated and reviewed',
      'Vendor approved and continuous monitoring begins',
    ],
    agents: [
      { name: 'Vendor Risk Agent', description: 'Assesses vendor financial and operational risk' },
      { name: 'Contract Review Agent', description: 'Analyzes contract terms and identifies risks' },
      { name: 'Compliance Verification Agent', description: 'Verifies vendor compliance documentation' },
    ],
    benefits: [
      '75% reduction in onboarding time',
      'Continuous risk monitoring',
      'Standardized vendor assessments',
      'Automated compliance verification',
    ],
  },
};

const WorkflowPage = () => {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const workflow = workflowData[workflowId];

  if (!workflow) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-2xl font-bold text-[#0F172A] mb-4">Workflow not found</h1>
          <Link to="/" className="text-[#1A3A8F] hover:underline">Return to homepage</Link>
        </div>
      </div>
    );
  }

  const WorkflowIcon = workflow.icon;

  return (
    <div className="min-h-screen bg-[#F6F7FB]">
      <Helmet>
        <title>{workflow.title} Workflow | Liquicent</title>
        <meta name="description" content={workflow.subtitle} />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-16 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
              style={{ backgroundColor: `${workflow.color}10` }}
            >
              <WorkflowIcon className="w-8 h-8" style={{ color: workflow.color }} />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
              {workflow.title}
            </h1>
            <p className="text-xl text-[#475569]">{workflow.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Problem & Solution */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-sm font-semibold text-[#64748B] uppercase tracking-wider mb-4">The Problem</h2>
              <p className="text-lg text-[#0F172A] leading-relaxed">{workflow.problem}</p>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[#2CB1A1] uppercase tracking-wider mb-4">The Solution</h2>
              <p className="text-lg text-[#0F172A] leading-relaxed">{workflow.solution}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Diagram */}
      <section className="py-16 bg-[#F6F7FB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-8">How It Works</h2>
          <Card className="p-8 bg-white border border-[#E5E7EB] rounded-2xl">
            <div className="space-y-4">
              {workflow.steps.map((step, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${workflow.color}10` }}
                  >
                    <span className="text-sm font-semibold" style={{ color: workflow.color }}>
                      {index + 1}
                    </span>
                  </div>
                  <div className="flex-1 p-4 bg-[#F6F7FB] rounded-lg">
                    <span className="text-[#0F172A] font-medium">{step}</span>
                  </div>
                  {index < workflow.steps.length - 1 && (
                    <div className="hidden md:block">
                      <ChevronRight className="w-5 h-5 text-[#94A3B8]" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </section>

      {/* Agents Used */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-8">Agents Used</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {workflow.agents.map((agent) => (
              <Card key={agent.name} className="p-6 bg-white border border-[#E5E7EB] rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-[#1A3A8F]/10 flex items-center justify-center mb-4">
                  <Bot className="w-6 h-6 text-[#1A3A8F]" />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-2">{agent.name}</h3>
                <p className="text-sm text-[#475569]">{agent.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-[#F6F7FB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-8">Enterprise Benefits</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {workflow.benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#2CB1A1] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span className="text-[#0F172A] font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Try This Workflow - Mini Lead Form */}
      <TryWorkflowForm workflow={workflow} />

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1A3A8F] to-[#142D70] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Run a Pilot</h2>
          <p className="text-lg text-white/80 mb-8">
            See how {workflow.title} automation can transform your organization
          </p>
          <Button 
            onClick={() => navigate('/request-demo')}
            className="bg-white text-[#1A3A8F] hover:bg-white/90 px-8 py-6 text-base font-medium"
          >
            Request Demo
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

// Try Workflow Mini-Form Component
const TryWorkflowForm = ({ workflow }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/leads/demo`, {
        full_name: formData.name,
        email: formData.email,
        company: formData.company,
        message: `Workflow Interest: ${workflow.title} - Try This Workflow form submission`,
      });

      setSubmitted(true);
      toast.success('Thank you! We\'ll be in touch shortly.');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-xl mx-auto px-6 lg:px-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[#2CB1A1]/10 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-[#2CB1A1]" />
          </div>
          <h3 className="text-xl font-bold text-[#0F172A] mb-2">Request Received!</h3>
          <p className="text-[#475569]">
            We'll reach out within 24 hours to set up your {workflow.title} pilot.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
              Try This Workflow
            </h2>
            <p className="text-[#475569] mb-6">
              See {workflow.title} automation in action. Fill out this quick form and we'll set up 
              a personalized pilot for your team.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#2CB1A1]/10 flex items-center justify-center">
                  <Check className="w-4 h-4 text-[#2CB1A1]" />
                </div>
                <span className="text-sm text-[#475569]">No commitment required</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#2CB1A1]/10 flex items-center justify-center">
                  <Check className="w-4 h-4 text-[#2CB1A1]" />
                </div>
                <span className="text-sm text-[#475569]">Personalized to your use case</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#2CB1A1]/10 flex items-center justify-center">
                  <Check className="w-4 h-4 text-[#2CB1A1]" />
                </div>
                <span className="text-sm text-[#475569]">Results in weeks, not months</span>
              </div>
            </div>
          </div>

          <Card 
            className="p-6 bg-[#F6F7FB] border border-[#E5E7EB] rounded-xl"
            style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-[#0F172A] text-sm">Name *</Label>
                <Input
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  className="mt-1 bg-white border-[#E5E7EB]"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-[#0F172A] text-sm">Work Email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@company.com"
                  className="mt-1 bg-white border-[#E5E7EB]"
                />
              </div>
              <div>
                <Label htmlFor="company" className="text-[#0F172A] text-sm">Company *</Label>
                <Input
                  id="company"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Acme Inc."
                  className="mt-1 bg-white border-[#E5E7EB]"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#1A3A8F] hover:bg-[#142C6D] text-white"
              >
                {loading ? 'Submitting...' : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Try {workflow.title}
                  </>
                )}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default WorkflowPage;
